import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useIntl } from "react-intl";
import ProjectPicker from "./ProjectPicker";

// The OPTIONAL "Link a project" field (Survey.project) in the survey builder.
// Linked, the assessment shows on the project's Evidence tab and its
// single-choice questions can be linked to the project's segments; left on
// "None", it belongs to no project. The (!) beside it says so.
//
// Locks once the assessment has a completed response (`locked`); the BE
// enforces that, this only shows it.
//
// `compact` (the classic builder's settings strip) moves the "Optional. …"
// helper line into the (!) tooltip, after the info, so the row is just the
// field. The locked notice stays visible under the field either way.
export function ImpactLinkPicker({
    id,
    projects,
    loading = false,
    value,
    onChange,
    locked = false,
    size = "small",
    compact = false,
    sx,
}) {
    const intl = useIntl();
    const help = intl.formatMessage({
        id: "project_picker.help",
        defaultMessage: "Optional. Single-choice questions can then ask about its segments.",
    });
    const info = intl.formatMessage({
        id: "project_picker.info",
        defaultMessage: "Link this assessment to a project to gather evidence for it: the assessment shows on the project's Evidence tab.",
    });
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0 }}>
            <ProjectPicker
                id={id}
                fullWidth
                size={size}
                sx={{ minWidth: 0, flex: 1, ...sx }}
                allowNone
                projects={projects}
                loading={loading}
                value={value}
                disabled={locked}
                onChange={onChange}
                label={intl.formatMessage({ id: "project_picker.link_label", defaultMessage: "Link a project" })}
                emptyText={intl.formatMessage({
                    id: "project_picker.no_eligible",
                    defaultMessage: "No projects yet",
                })}
                helperText={
                    locked
                        ? intl.formatMessage({
                              id: "project_picker.locked",
                              defaultMessage: "Locked: this assessment already has completed responses.",
                          })
                        : compact
                          ? null
                          : help
                }
            />
            {/* Centred on the field itself, not on the field + helper text. */}
            <Box sx={{ height: size === "medium" ? 56 : 40, display: "flex", alignItems: "center" }}>
                <Tooltip title={compact ? <TooltipLines lines={[info, help]} /> : info} enterTouchDelay={0} arrow>
                    <IconButton size="small" aria-label={compact ? `${info} ${help}` : info} data-testid={`${id}-info`}>
                        <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
}

// Several sentences in one tooltip, one per line.
function TooltipLines({ lines }) {
    return lines.map((line) => (
        <Box key={line} component="span" sx={{ display: "block", "& + &": { mt: 0.5 } }}>
            {line}
        </Box>
    ));
}
